// App-shell — error boundary around routed pages.
//
// react-router v7 navigates via transitions; if a page throws during
// render there's no boundary, React aborts the pending transition and
// keeps showing the PREVIOUS page while the URL updates — i.e. "clicked
// the nav, URL changed, but the page didn't". This boundary turns that
// silent ghost state into a visible error (and logs the real cause), so a
// broken page is diagnosable instead of looking like nav is dead.
//
// Keyed by pathname at the call site (AppShell), so navigating away
// remounts a fresh boundary and the next page renders normally.

import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface the real error + component stack for local debugging.
    console.error("Route render error:", error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <main className="mx-auto flex w-full max-w-xl flex-col gap-4 p-6 text-brand-paper-100">
          <h1 className="m-0 font-heading text-2xl text-brand-gold-300">
            This page hit a snag
          </h1>
          <p className="m-0 font-body text-sm text-brand-paper-100/80">
            Something went wrong rendering this page. The full error is in the
            browser console. Use the nav above to move on.
          </p>
          <pre className="overflow-x-auto rounded-md border border-brand-gold-500/20 bg-brand-forest-800 p-3 text-xs text-brand-paper-100/70">
            {this.state.error.message}
          </pre>
        </main>
      );
    }
    return this.props.children;
  }
}
